/**
 * Feed global del `decision_log` — la ventana al modo shadow (roadmap §F2.b).
 *
 * Responde a "¿qué decidió el motor y con qué reparto?" mientras los gates de
 * mercado están OFF: cada evaluación queda registrada con su veredicto y su
 * motivo legible, sin haber enviado nada. Es la pantalla de monitorización que
 * permite comparar el motor contra lo que Ops hace a mano antes de activarlo.
 *
 * ⚠️ El back NO expone `context` en este feed (sólo en el detalle del
 * episodio) porque arrastra PII del payload con el que se decidió: aquí se
 * estaría paseando por una pantalla de monitorización de miles de clientes.
 */

import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listDecisionLog, useMsOpsUnavailable } from "../../api/msops";
import { useAuth } from "../../context/AuthContext";
import {
  DECISION_KINDS,
  DECISION_LABEL,
  DECISION_STYLES,
  DecisionKind,
  DecisionLogItem,
  Paginated,
} from "../../types/msops";
import Pagination from "../OnboardingCustomers/components/Pagination";
import {
  Badge,
  buttonStyle,
  COLORS,
  Card,
  describeError,
  EmptyRow,
  ErrorBanner,
  fieldStyle,
  fmtDateTime,
  inputStyle,
  labelStyle,
  Loading,
  secondaryButtonStyle,
  Td,
  Th,
  UnavailableNotice,
} from "./components/ui";

const DEFAULT_PAGE_SIZE = 50;
const REASON_DEBOUNCE_MS = 300;

export default function ShadowFeed() {
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [data, setData] = useState<Paginated<DecisionLogItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [verdict, setVerdict] = useState<DecisionKind | "">("");
  const [reason, setReason] = useState("");
  const [debouncedReason, setDebouncedReason] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedReason(reason), REASON_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [reason]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await listDecisionLog(
        {
          page: page + 1,
          pageSize,
          verdict: verdict || undefined,
          reason: debouncedReason || undefined,
        },
        { token },
      );
      setData(response);
    } catch (err) {
      setError(describeError(err, "No se pudo cargar el decision log"));
    } finally {
      setLoading(false);
    }
  }, [token, page, pageSize, verdict, debouncedReason]);

  useEffect(() => {
    load();
  }, [load]);

  // 404 del passthrough: ms-ops no está configurado en este entorno.
  if (unavailable) {
    return <UnavailableNotice />;
  }

  if (loading && !data) {
    return <Loading label="Cargando decisiones…" />;
  }

  const rows = data?.items ?? [];

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1400, margin: "0 auto" }}>
      <Link
        to="/operations/recobros"
        style={{ color: COLORS.muted, fontSize: ".8rem", textDecoration: "none" }}
      >
        ← Recobros
      </Link>

      <div style={{ margin: "0.5rem 0 0.85rem" }}>
        <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
          Decisiones del motor (shadow)
        </h2>
        <p
          style={{
            margin: "0.25rem 0 0",
            color: COLORS.muted,
            fontSize: ".85rem",
          }}
        >
          Qué evaluó el motor y por qué. En modo shadow las decisiones se
          registran sin enviar nada.
        </p>
      </div>

      <Card style={{ padding: "0.75rem 0.95rem", marginBottom: "0.65rem" }}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            alignItems: "flex-end",
          }}
        >
          <label style={fieldStyle}>
            <span style={labelStyle}>Veredicto</span>
            <select
              value={verdict}
              onChange={(e) => {
                setVerdict(e.target.value as DecisionKind | "");
                setPage(0);
              }}
              style={inputStyle}
            >
              <option value="">Todos</option>
              {DECISION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {DECISION_LABEL[k]}
                </option>
              ))}
            </select>
          </label>

          <label style={{ ...fieldStyle, flex: "1 1 260px" }}>
            <span style={labelStyle}>Motivo contiene</span>
            <input
              type="search"
              value={reason}
              placeholder="quiet hours, sin plantilla…"
              onChange={(e) => {
                setReason(e.target.value);
                setPage(0);
              }}
              style={inputStyle}
            />
          </label>

          <button
            type="button"
            onClick={() => {
              setVerdict("");
              setReason("");
              setPage(0);
            }}
            style={secondaryButtonStyle}
          >
            Limpiar
          </button>

          <button type="button" onClick={load} style={buttonStyle}>
            Actualizar
          </button>
        </div>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card
        style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s ease" }}
      >
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: ".85rem",
            }}
          >
            <thead>
              <tr style={{ background: COLORS.subtle }}>
                <Th>Evaluado</Th>
                <Th>Veredicto</Th>
                <Th>Motivo</Th>
                <Th>Episodio</Th>
                <Th>Paso</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <EmptyRow colSpan={5}>
                  Sin decisiones para los filtros aplicados
                </EmptyRow>
              ) : (
                rows.map((item) => {
                  const style = DECISION_STYLES[item.decision] ?? {
                    bg: "#f8f9fb",
                    fg: "#73879C",
                  };
                  return (
                    <tr
                      key={item.id}
                      style={{ borderBottom: `1px solid ${COLORS.rowBorder}` }}
                    >
                      <Td style={{ whiteSpace: "nowrap" }}>
                        {fmtDateTime(item.evaluatedAt)}
                      </Td>
                      <Td>
                        <div
                          style={{
                            display: "flex",
                            gap: "0.3rem",
                            flexWrap: "wrap",
                          }}
                        >
                          <Badge bg={style.bg} fg={style.fg}>
                            {DECISION_LABEL[item.decision as DecisionKind] ??
                              item.decision}
                          </Badge>
                          {item.dryRun && (
                            <Badge bg="#f8f9fb" fg="#73879C">
                              dry-run
                            </Badge>
                          )}
                        </div>
                      </Td>
                      <Td>{item.reason}</Td>
                      <Td mono style={{ fontSize: ".75rem" }}>
                        <Link
                          to={`/operations/recobros/${item.episodeId}`}
                          style={{ color: COLORS.link, textDecoration: "none" }}
                        >
                          {item.episodeId}
                        </Link>
                      </Td>
                      <Td mono style={{ fontSize: ".75rem" }}>
                        {item.stepId ?? "—"}
                      </Td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {data && data.total > 0 && (
          <Pagination
            page={page}
            pageSize={pageSize}
            total={data.total}
            onPageChange={(p) => setPage(p)}
            onPageSizeChange={(s) => {
              setPageSize(s);
              setPage(0);
            }}
            disabled={loading}
          />
        )}
      </Card>
    </div>
  );
}
